import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Handle from '~/prix/react/components/handle'
import Modal from '~/prix/react/components/modal'
import useAPI from '~/prix/react/hooks/api'
import recommendedLayerForVariable from '../variable/recommendedLayerForVariable.util'
import { Layer } from '../variable/spaceTime.types'
import {
  Variable,
  VariableAndOptionalResult,
  VariableWithConclusion,
} from '../variable/variables.typing'

import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import useAction from '~/prix/react/hooks/action'
import useDebounce from 'react-use/lib/useDebounce'

import { NotFoundInfo } from './styles/variablesView'
import categories from '../categories'
import InfiniteScroll from './viewSelectModalVariable.scroll'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList } from '@fortawesome/free-solid-svg-icons'
import WrapperFilters from './viewSelectModalVariable.filters'
import { useMedia } from 'react-use'
import { Expression } from '~/prix/types/expression'
import { ShapeBase } from '~/prix'
import { BaseWrapper } from './styles/baseModalWrapper'
import chroma from 'chroma-js'
import viewListDefinition from '../view/list/viewList.definition'
import { ToolsAndOptionalResult } from '../tools/tools.typing'
import { useLocation, useNavigate } from 'react-router'
import { VariableCollectionsAndOptionalResult } from '../variableCollection/variableCollection.typing'

const WrapperModalVariable = styled(BaseWrapper)`
  .content {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
  }
  .filters {
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .search {
    flex: 0 0 auto;
    position: relative;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-top-right-radius: 8px;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      border-top-left-radius: 8px;
      border-top-right-radius: 0px;
      width: 85%;
    }

    input {
      background-color: transparent;
      width: 100%;
      padding: 1rem;
      border: none;
      outline: none;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        border-bottom: 0;
      }
    }
  }

  .filter-category {
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      width: 15%;
      border-radius: 8px;
      justify-content: center;
      align-items: center;
    }
  }
`

const Variables = styled(BaseWrapper)`
  flex: 1;
  width: 100%;

  .variable {
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: space-between;
    gap: 3px;
    padding: 1rem 1rem;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      margin-left: 1rem;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
    }

    &:hover {
      background-color: ${({ theme }) => chroma(theme.colors.lightGrey).darken(0.15).hex()};
    }

    &.is-selected,
    &.is-selected:hover {
      background-color: ${({ theme }) => theme.colors.lightGrey};
    }

    .alert {
      padding: 0.3rem 1rem;
      border-radius: 8px;
      margin-right: 0.3rem;
      text-transform: uppercase;
      font-weight: bold;
      white-space: nowrap;
    }

    .button {
      user-select: none;
      cursor: pointer;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      padding: 0.3rem 1rem;
      border-radius: 8px;
      margin-right: 0.3rem;
      border: none;
      white-space: nowrap;
    }
  }
`

const ButtonFilter = styled.button`
  display: flex;
  flex: 1;
  background: transparent;
  box-shadow: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  color: #0c2340;
  margin: 0.2rem;
  height: 95%;
  border-radius: 8px;
`

const ActionsContainer = styled.div`
  display: flex;
  padding: 10px;
  height: 50px;
  justify-content: flex-end;
  gap: 7px;
`

const CancelButton = styled(ButtonLinkAsSimpleText)`
  margin: 0;
`

const ConfirmButton = styled(ButtonLink)`
  margin: 0;
`
/* 
Essas sectorKeys do categories.data.ts utilizam o label como nome da categoria diferente das demais
*/
const sectorKeysUsingLabelAsTitle = new Set([
  'monitorServiceOverview',
  'monitorOnlineCourses',
  'monitorAppSebrae',
  'agentAttendanceSebrae',
  'monitorEmissorNfe',
  'monitorConecta',
])

interface ViewSelectModalVariableProps {
  variables: VariableWithConclusion[]
  isOpen: boolean
  onClose: () => void
  layer: Layer
  baseLayer: Layer
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  stateId: number | null
  stateName: string | null
}

type VariableWithDetailsAndResult = ToolsAndOptionalResult &
  VariableAndOptionalResult &
  VariableCollectionsAndOptionalResult

function decomposeExpression(expression: Expression | undefined): Expression[] {
  if (!expression) return []

  const filters: Expression[] = []

  const flattenExpression = (expr: Expression, operator: 'AND' | 'OR') => {
    if (expr.kind === 'binary' && expr.operator === operator) {
      if (expr.left) flattenExpression(expr.left as Expression, operator)
      if (expr.right) flattenExpression(expr.right as Expression, operator)
    } else {
      filters.push(expr)
    }
  }

  const topLevelFilters: Expression[] = []
  const flattenAnd = (expr: Expression) => {
    if (expr.kind === 'binary' && expr.operator === 'AND') {
      if (expr.left) flattenAnd(expr.left as Expression)
      if (expr.right) flattenAnd(expr.right as Expression)
    } else {
      topLevelFilters.push(expr)
    }
  }
  flattenAnd(expression)

  const allFilters: Expression[] = []
  topLevelFilters.forEach(filter => {
    if (filter.kind === 'binary' && filter.operator === 'OR') {
      const orFilters: Expression[] = []
      const flattenOr = (expr: Expression) => {
        if (expr.kind === 'binary' && expr.operator === 'OR') {
          if (expr.left) flattenOr(expr.left as Expression)
          if (expr.right) flattenOr(expr.right as Expression)
        } else {
          orFilters.push(expr)
        }
      }
      flattenOr(filter)
      allFilters.push(...orFilters)
    } else {
      allFilters.push(filter)
    }
  })

  return allFilters
}

function getExpressionLeftProperty(expression: Expression): string | undefined {
  if (expression.kind !== 'binary' || expression.left?.kind !== 'property') {
    return undefined
  }

  return String(expression.left.property)
}

function isCompleteFilter(expression: Expression): boolean {
  return (
    expression?.kind === 'binary' &&
    Boolean(expression?.right) &&
    Object.keys(expression.right).length > 0
  )
}

function isExpressionInFilterableBy(
  expression: Expression,
  filterableBy: ShapeBase | undefined,
): boolean {
  const leftProperty = getExpressionLeftProperty(expression)

  if (!leftProperty || !filterableBy) {
    return false
  }

  return Object.prototype.hasOwnProperty.call(filterableBy, leftProperty)
}

function buildFilterExpression(filters: Expression[]): Expression | undefined {
  // Group '=' filters by property so that filters on the same property are OR'ed together
  const equalityGroups: Record<string, Expression[]> = {}
  const remainingFilters: Expression[] = []

  filters.forEach(filter => {
    const propertyKey = getExpressionLeftProperty(filter)

    if (filter.kind === 'binary' && filter.operator === '=' && propertyKey) {
      if (!equalityGroups[propertyKey]) {
        equalityGroups[propertyKey] = []
      }
      equalityGroups[propertyKey].push(filter)
    } else {
      remainingFilters.push(filter)
    }
  })

  const processedFilters: Expression[] = [...remainingFilters]
  Object.values(equalityGroups).forEach(group => {
    if (group.length === 1) {
      processedFilters.push(group[0])
      return
    }

    const orExpression = group.reduce<Expression | undefined>((acc, filter) => {
      if (!acc) return filter

      return {
        type: 'expression',
        kind: 'binary',
        left: acc,
        operator: 'OR',
        right: filter,
      }
    }, undefined)

    if (orExpression) {
      processedFilters.push(orExpression)
    }
  })

  return processedFilters.reduce<Expression | undefined>((acc, filter) => {
    if (!acc) {
      return filter
    }

    return {
      type: 'expression',
      kind: 'binary',
      left: acc,
      operator: 'AND',
      right: filter,
    }
  }, undefined)
}

export default function ViewSelectModalVariable({
  variables,
  isOpen,
  onClose,
  layer,
  baseLayer,
  onChangeLayer,
  stateId,
  stateName,
}: ViewSelectModalVariableProps) {
  const { context, isLoading: isLoadingAPI } = useAPI()
  const [currentBaseLayer, setCurrentBaseLayer] = useState<Layer>(baseLayer)
  const [currentCategory, setCurrentCategory] = useState<typeof categories>(
    (layer.variable as Variable)?.filter && (layer.variable as Variable)?.categoryKey
      ? {
          [layer.variable.categoryKey]: {
            title: categories[layer.variable.categoryKey].title,
            sectorKey: categories[layer.variable.categoryKey].sectorKey,
          },
        }
      : {
          all: { title: 'Tudo', sectorKey: 'general' },
        },
  )
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const isDebouncing = search !== debouncedSearch && search !== '' && debouncedSearch !== ''
  const [collapsedStates, setCollapsedStates] = useState<{ [key: string]: boolean }>(
    (layer.variable as Variable)?.filter
      ? {
          [`${layer.variable?.title}-${layer.variable?.label}`]: true,
        }
      : {},
  )
  const [variablesPaginated, setVariablesPaginated] = useState<VariableWithConclusion[]>([])
  const [indexes, setIndexes] = useState({ startIndex: 0, endIndex: 50 })
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasChangedVariableLayer, setHasChangedVariableLayer] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const initialFilterableBy = (layer.variable as Variable)?.filter
    ? (layer.variable as Variable)?.filterableBy?.shape
    : undefined
  const [filters, setFilters] = useState<Expression[] | undefined>(
    (layer.variable as Variable)?.filter
      ? decomposeExpression((layer.variable as Variable)?.filter).filter(filter =>
          isExpressionInFilterableBy(filter, initialFilterableBy),
        )
      : undefined,
  )
  const [filterableBy, setFilterableBy] = useState<ShapeBase | undefined>(initialFilterableBy)
  const isMobile = useMedia('(max-width: 768px)')

  const isTool = useMemo(() => {
    return !!(currentBaseLayer.variable as ToolsAndOptionalResult).path
  }, [currentBaseLayer])

  const navigate = useNavigate()

  const handleNavigation = (layerWithFilter?: Layer) => {
    if (isTool) {
      const path = (currentBaseLayer.variable as ToolsAndOptionalResult).path
      if (path && location.pathname !== path) {
        const state = {
          replace: true,
          state: {
            currentBaseLayer: layerWithFilter ?? currentBaseLayer,
          },
        }
        navigate(path, state)
      }
    }
  }

  useEffect(() => {
    setCurrentBaseLayer(baseLayer)
  }, [layer])

  useDebounce(
    () => {
      if (!search) {
        if (debouncedSearch) {
          setVariablesPaginated([])
          setHasChangedVariableLayer(true)
        }
      }
      setDebouncedSearch(search)
      setIndexes({ startIndex: 0, endIndex: 50 })
    },
    1500,
    [search],
  )
  const categoryKey = currentCategory && Object.keys(currentCategory)[0]

  const filteredVariablesResult = useAction(
    viewListDefinition,
    {
      search: debouncedSearch,
      category: categoryKey,
      startIndex: indexes.startIndex,
      endIndex: indexes.endIndex,
    },
    [debouncedSearch, indexes.startIndex, indexes.endIndex, categoryKey],
    {
      autoLoad: (Boolean(context.user) && !isLoadingAPI) || isDebouncing,
      staleWhileRevalidate: false,
    },
  )

  useEffect(() => {
    const result = filteredVariablesResult.result
    if (!result) return

    const items = (result.items ?? []).filter(
      (variable: VariableWithDetailsAndResult) => variable.isListed !== false,
    )

    setVariablesPaginated(prev => {
      if (indexes.startIndex === 0) {
        return items
      }

      return [...prev, ...items]
    })

    setIsLoadingMore(false)
    setHasChangedVariableLayer(false)
  }, [filteredVariablesResult.result])

  const getFilter = useCallback((expressions: Record<number, Expression> | undefined) => {
    const newFilters = expressions ? Object.values(expressions) : []
    setFilters(newFilters)
  }, [])

  const handleSelectVariable = useCallback(
    (variable: VariableWithConclusion) => {
      if (!context.user) return

      const currentBase = currentBaseLayer?.variable
      const keyChanged = currentBase?.key !== variable.key
      const presetKeyChanged = currentBase?.presetKey !== variable.presetKey
      if (keyChanged || presetKeyChanged) {
        setFilters([])
      }

      setCurrentBaseLayer(current =>
        recommendedLayerForVariable(
          variable,
          context.user?.roles.some(
            item =>
              item === 'systemAdministrator' || item === 'administrator' || item === 'manager',
          ),
          stateId,
          stateName,
          current,
        ),
      )

      const filterableBy = variable?.filterableBy?.shape
      setFilterableBy(filterableBy)
    },
    [context.user, currentBaseLayer, filters],
  )

  const handleCancel = () => {
    onClose()
    setCurrentBaseLayer(layer)
    setCollapsedStates({})
    setSearch('')
  }

  const handleConfirm = () => {
    const expressions = filters || []

    const validFilters = expressions.filter(isCompleteFilter)
    const filtersWithoutFilterableBy = validFilters.filter(
      filter => !isExpressionInFilterableBy(filter, filterableBy),
    )
    const filtersWithFilterableBy = validFilters.filter(filter =>
      isExpressionInFilterableBy(filter, filterableBy),
    )

    const nativeFiltersFromCurrentLayer = (currentBaseLayer.variable as Variable)?.filter
      ? decomposeExpression((currentBaseLayer.variable as Variable)?.filter)
          .filter(isCompleteFilter)
          .filter(filter => !isExpressionInFilterableBy(filter, filterableBy))
      : []
    const nativeFilters = buildFilterExpression(
      filtersWithoutFilterableBy.length > 0
        ? filtersWithoutFilterableBy
        : nativeFiltersFromCurrentLayer,
    )
    const interleavedFilters = buildFilterExpression(filtersWithFilterableBy)

    let finalFilter: Expression | undefined
    if (nativeFilters && interleavedFilters) {
      finalFilter = {
        type: 'expression',
        kind: 'binary',
        left: nativeFilters,
        operator: 'AND',
        right: interleavedFilters,
      }
    } else {
      finalFilter = nativeFilters || interleavedFilters
    }

    const withFilter = {
      ...currentBaseLayer,
      variable: {
        ...currentBaseLayer.variable,
        filter: finalFilter,
      },
    }
    setFilters(filtersWithFilterableBy)
    onChangeLayer(withFilter)

    handleNavigation(withFilter)
    onClose()
  }

  const getCollapsedOpen = (isOpen: boolean, key: string | undefined) => {
    if (key) {
      const newState = { [key]: isOpen }
      setCollapsedStates(newState)
    }
  }

  const categoriesInfo = useMemo(
    () =>
      Object.entries(categories).map(([key, value]) => {
        const shouldUseLabel = sectorKeysUsingLabelAsTitle.has(value.sectorKey)
        const displayTitle = shouldUseLabel && value.label ? value.label : value.title

        return {
          key,
          title: displayTitle,
          sectorKey: value.sectorKey,
          parentKey: value.parentKey,
          childrenKeys: value.childrenKeys,
          defaultChildKey: value.defaultChildKey,
        }
      }),
    [],
  )

  const handleClick = (info: { key: string; title: string; sectorKey: string }) => {
    const currentCategoryKey = Object.keys(currentCategory)[0]
    const shouldReloadVariables =
      info.key !== currentCategoryKey ||
      search !== '' ||
      debouncedSearch !== '' ||
      indexes.startIndex !== 0 ||
      indexes.endIndex !== 50

    if (!shouldReloadVariables) {
      setCollapsedStates({})
      return
    }

    setVariablesPaginated([])
    setCollapsedStates({})

    setSearch('')
    setDebouncedSearch('')

    setHasChangedVariableLayer(true)

    setCurrentCategory({
      [info.key]: {
        title: info.title,
        sectorKey: info.sectorKey,
      },
    })
    setIndexes({ startIndex: 0, endIndex: 50 })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalStyle='sentinel' baseWidth={'925px'}>
      <WrapperModalVariable
        $flexDirection='row'
        $height={isMobile ? '65dvh' : '72vh'}
        $justifyLeftContent='space-around'
      >
        {!isMobile && (
          <WrapperFilters
            categoriesInfo={categoriesInfo}
            currentCategory={currentCategory}
            handleClick={handleClick}
            setIsFilterOpen={setIsFilterOpen}
            isMobile={isMobile}
            isModalOpen={isOpen}
            variables={variables}
          />
        )}
        <div className='content'>
          <div className='filters'>
            <div className='search'>
              <input
                type='search'
                placeholder='Buscar...'
                onChange={e => setSearch(e.target.value)}
                value={search}
              />
            </div>
            <div className='filter-category'>
              <ButtonFilter onClick={() => setIsFilterOpen(!isFilterOpen)}>
                <FontAwesomeIcon icon={faList} />
                {isFilterOpen && (
                  <Modal
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                    modalStyle='sentinel'
                  >
                    <WrapperModalVariable
                      $flexDirection='row'
                      $height={isMobile ? '65dvh' : '72vh'}
                      $justifyLeftContent='space-around'
                    >
                      <WrapperFilters
                        categoriesInfo={categoriesInfo}
                        currentCategory={currentCategory}
                        handleClick={handleClick}
                        setIsFilterOpen={setIsFilterOpen}
                        isMobile={isMobile}
                        isModalOpen={isOpen}
                      />
                    </WrapperModalVariable>
                  </Modal>
                )}
              </ButtonFilter>
            </div>
          </div>
          <Handle
            isLoading={
              isDebouncing ||
              (filteredVariablesResult.isLoading && !variablesPaginated.length && !isLoadingMore) ||
              hasChangedVariableLayer
            }
          >
            <Variables>
              {!filteredVariablesResult.isLoading && variablesPaginated.length === 0 ? (
                <NotFoundInfo>
                  <p>Sem resultados encontrados</p>
                </NotFoundInfo>
              ) : (
                <InfiniteScroll
                  variables={variablesPaginated}
                  currentBaseLayer={currentBaseLayer}
                  handleSelectVariable={handleSelectVariable}
                  getCollapsedOpen={getCollapsedOpen}
                  collapsedStates={collapsedStates}
                  setIndexes={setIndexes}
                  nextPages={filteredVariablesResult.result?.pagesLeft || 0}
                  apiIsLoading={filteredVariablesResult.isLoading}
                  setIsLoadingMore={setIsLoadingMore}
                  selectedVariableIsFilterableBy={filterableBy}
                  getFilter={getFilter}
                  filters={filters}
                />
              )}
            </Variables>
            <ActionsContainer>
              <CancelButton onGo={handleCancel}>Cancelar</CancelButton>
              <ConfirmButton onGo={handleConfirm}>Confirmar</ConfirmButton>
            </ActionsContainer>
          </Handle>
        </div>
      </WrapperModalVariable>
    </Modal>
  )
}
